import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, Sparkles } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { buildWhatsAppUrl } from "@/data/translations";

export default function FinalCTA() {
  const { t, isRTL } = useLanguage();

  return (
    <section
      data-testid="final-cta-section"
      className="bg-[#FAFAFA] px-5 py-20 md:px-8 md:py-28 dark:bg-[#080808]"
    >
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="relative overflow-hidden rounded-[32px] border border-black/6 bg-[linear-gradient(135deg,#FFF7F0_0%,#FFFFFF_48%,#FFF0E5_100%)] p-10 text-center text-[#111] shadow-[0_30px_80px_rgba(16,24,40,0.12)] grain-overlay md:p-16 lg:p-20 dark:border-white/10 dark:bg-[linear-gradient(135deg,#101010_0%,#080808_52%,#151515_100%)] dark:text-white dark:shadow-[0_30px_80px_rgba(0,0,0,0.38)]"
        >
          {/* Ambient glows */}
          <div className="absolute -top-24 -left-24 w-[400px] h-[400px] rounded-full bg-[#F37021]/30 blur-[120px] pointer-events-none" />
          <div className="absolute -bottom-24 -right-24 w-[400px] h-[400px] rounded-full bg-[#F37021]/25 blur-[120px] pointer-events-none" />

          {/* Grid pattern */}
          <svg className="absolute inset-0 w-full h-full opacity-[0.05] pointer-events-none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid2" width="50" height="50" patternUnits="userSpaceOnUse">
                <path d="M 50 0 L 0 0 0 50" fill="none" stroke="currentColor" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid2)" />
          </svg>

          <div className="relative">
            <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-[#F37021]/40 bg-[#F37021]/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-[#F37021]">
              <Sparkles className="h-3.5 w-3.5" />
              {t.finalCta.eyebrow}
            </div>

            <h2 className="mx-auto max-w-4xl font-display text-[36px] font-bold leading-[1.08] sm:text-5xl md:text-6xl lg:text-7xl">
              {t.finalCta.title}
            </h2>

            <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-[#5B6473] md:mt-7 md:text-xl dark:text-[#A3A3A3]">
              {t.finalCta.subtitle}
            </p>

            <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row sm:gap-4">
              <a
                href="#countries"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.querySelector("#countries");
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="group inline-flex h-14 items-center justify-center gap-2 rounded-full bg-[#F37021] px-8 font-medium text-white shadow-[0_14px_30px_rgba(243,112,33,0.35)] transition-all hover:-translate-y-0.5 hover:bg-[#E06015]"
                data-testid="final-cta-start-shopping"
              >
                {t.finalCta.cta1}
                <ArrowRight className={`w-5 h-5 transition-transform group-hover:translate-x-1 ${isRTL ? 'rotate-180 group-hover:-translate-x-1 group-hover:translate-x-0' : ''}`} />
              </a>
              <a
                href={buildWhatsAppUrl(t.common.whatsappMsg)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-14 items-center justify-center gap-2 rounded-full border border-black/10 bg-white/82 px-8 font-medium text-[#111] transition-all hover:-translate-y-0.5 hover:border-[#25D366]/45 hover:text-[#25D366] dark:border-white/20 dark:bg-white/10 dark:text-white dark:hover:bg-white dark:hover:text-[#111]"
                data-testid="final-cta-whatsapp"
              >
                <MessageCircle className="w-5 h-5" />
                {t.finalCta.cta2}
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
